import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChoiceChips, TextField } from '@repo/design-system-kit';
import { SelectCountry } from '@repo/shared-kit/components';
import cx from 'clsx';
import { IbanField } from '../../form-fields/iban-field';
import styles from './mandate-invitation-form.module.css';

type ClientType = 'individual' | 'company';

interface MandateInvitationFormProps {
  clientType?: ClientType;
  firstName?: string;
  lastName?: string;
  email?: string;
  address?: string;
  zipcode?: string;
  city?: string;
  country?: string;
  iban?: string;
  onUpdate: (field: string, value: string) => void;
}

export function MandateInvitationForm({
  clientType = 'individual',
  firstName,
  lastName,
  email,
  address,
  zipcode,
  city,
  country,
  iban,
  onUpdate,
}: MandateInvitationFormProps) {
  const { formatMessage } = useIntl();

  const handleClientTypeChange = useCallback(
    (value: ClientType) => {
      onUpdate('clientType', value);
    },
    [onUpdate]
  );

  return (
    <div className={styles.formContainer} data-testid="mandates-invitation-details">
      <h3 className="title-3 mb-16" data-testid="details-title">
        <FormattedMessage id="sdd-collections.payment-link-page.details" />
      </h3>

      <ChoiceChips
        className={cx(styles.row, styles.clientTypeRow)}
        data-testid="client-type"
        onChange={handleClientTypeChange}
        options={[
          {
            value: 'individual' as const,
            label: <FormattedMessage id="client-type.individual" />,
          },
          {
            value: 'company' as const,
            label: <FormattedMessage id="client-type.company" />,
          },
        ]}
        value={clientType}
      />

      <div className={cx(styles.row, styles.nameFields)}>
        <TextField
          data-testid="first-name"
          label={formatMessage({ id: 'labels.name' })}
          onChange={value => {
            onUpdate('firstName', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-first-name' })}
          type="text"
          value={firstName}
        />

        <TextField
          data-testid="last-name"
          label={formatMessage({ id: 'labels.last-name' })}
          onChange={value => {
            onUpdate('lastName', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-last-name' })}
          type="text"
          value={lastName}
        />
      </div>

      <TextField
        className={styles.row}
        data-testid="email"
        label={formatMessage({ id: 'labels.email' })}
        onChange={value => {
          onUpdate('email', value);
        }}
        placeholder={formatMessage({ id: 'placeholders.fake-email' })}
        type="email"
        value={email}
      />

      <TextField
        className={styles.row}
        data-testid="address"
        label={formatMessage({ id: 'labels.address' })}
        onChange={value => {
          onUpdate('address', value);
        }}
        placeholder={formatMessage({ id: 'placeholders.fake-address' })}
        type="text"
        value={address}
      />

      <div className={cx(styles.row, styles.zipcodeCity)}>
        <TextField
          data-testid="zipcode"
          label={formatMessage({ id: 'labels.zipcode' })}
          onChange={value => {
            onUpdate('zipcode', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-zipcode' })}
          type="text"
          value={zipcode}
        />

        <TextField
          data-testid="city"
          label={formatMessage({ id: 'labels.city' })}
          onChange={value => {
            onUpdate('city', value);
          }}
          placeholder={formatMessage({ id: 'placeholders.fake-city' })}
          type="text"
          value={city}
        />
      </div>

      <SelectCountry
        className={styles.row}
        data-testid="country"
        label={formatMessage({ id: 'labels.country' })}
        onSelectionChange={value => {
          onUpdate('country', String(value));
        }}
        placeholder={formatMessage({ id: 'placeholders.country' })}
        selectedKey={country}
      />

      <IbanField
        className={styles.row}
        data-testid="iban"
        onUpdate={value => {
          onUpdate('iban', value);
        }}
        value={iban}
      />
    </div>
  );
}
